import React, { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';

const StatsBar = ({ allLeads = [] }) => {
    const [animatedRevenue, setAnimatedRevenue] = useState(0);
    const [animatedAccounts, setAnimatedAccounts] = useState(0);
    const [animatedCustomers, setAnimatedCustomers] = useState(0);

    // Calculate real stats from leads and customers
    // 'value' field maps to actual_sales for customers and forecasted_sales for leads
    const totalRevenue = allLeads.reduce((sum, lead) => {
        // Use Level 1 Category 'current_customers' instead of raw status 'active'
        if (lead.category === 'current_customers') {
            return sum + (lead.value || 0);
        }
        return sum;
    }, 0);

    const activeAccounts = allLeads.filter(lead => lead.category === 'current_customers').length;
    const newCustomers = allLeads.filter(lead => lead.category === 'leads').length;

    // Animate numbers on mount
    useEffect(() => {
        const revenueInterval = setInterval(() => {
            setAnimatedRevenue(prev => {
                const target = totalRevenue || 825370;
                if (prev < target) return prev + Math.ceil(target / 50);
                return target;
            });
        }, 30);

        const accountsInterval = setInterval(() => {
            setAnimatedAccounts(prev => {
                const target = activeAccounts || 40153;
                if (prev < target) return prev + Math.ceil(target / 50);
                return target;
            });
        }, 30);

        const customersInterval = setInterval(() => {
            setAnimatedCustomers(prev => {
                const target = newCustomers || 550;
                if (prev < target) return prev + Math.ceil(target / 50);
                return target;
            });
        }, 30);

        return () => {
            clearInterval(revenueInterval);
            clearInterval(accountsInterval);
            clearInterval(customersInterval);
        };
    }, [totalRevenue, activeAccounts, newCustomers]);

    return (
        <div className="bg-white border-b border-gray-200 p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {/* Total Revenue */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Total Revenue</span>
                        <span className="text-emerald-600 text-xs font-semibold flex items-center gap-1">
                            +97.5% <TrendingUp className="w-3 h-3" />
                        </span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                        ${animatedRevenue.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Trending up this month</div>
                </div>

                {/* New Customers */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">New Customers</span>
                        <span className="text-red-600 text-xs font-semibold">-17%</span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {animatedCustomers.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Down 62% this period</div>
                </div>

                {/* Active Accounts */}
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Active Accounts</span>
                        <span className="text-emerald-600 text-xs font-semibold flex items-center gap-1">
                            +40.2% <TrendingUp className="w-3 h-3" />
                        </span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {animatedAccounts.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Strong user retention</div>
                </div>

                {/* Growth Rate */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Growth Rate</span>
                        <span className="text-emerald-600 text-xs font-semibold flex items-center gap-1">
                            +3.3% <TrendingUp className="w-3 h-3" />
                        </span>
                    </div>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900">6.2%</div>
                    <div className="text-xs text-gray-500 mt-1">Steady performance</div>
                </div>
            </div>
        </div>
    );
};

export default StatsBar;
